import { Box, Drawer, styled, Typography } from '@mui/material';
import { Row } from 'further-ui/components/Layout';
import { PropsWithChildren } from 'react';
import CloseButton from './CloseButton';

const drawerSize = {
  md: 800,
  lg: 1100,
  xl: 1400,
};

const DrawerContainer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: 'md' | 'lg' | 'xl' }>(({ theme, size }) => ({
  width: '100%',
  maxWidth: drawerSize[size],
  zIndex: 2000,
  '& .MuiDrawer-paper': {
    width: '100%',
    maxWidth: drawerSize[size],
    borderTopLeftRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: theme.shape.borderRadius,
  },
}));

const DrawerTitle = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.lg),
}));

const DrawerContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'innerScroll',
})<{ innerScroll: boolean }>(({ theme, innerScroll }) => ({
  padding: theme.spacing(0, theme.spacingSizes.lg),
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  overflowY: innerScroll ? 'auto' : undefined,
}));

const DrawerActions = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.lg),
}));

type Props = {
  open: boolean;
  onClose?: () => void;
  title: React.ReactNode;
  actions: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
  innerScroll?: boolean;
};

const FullPageDrawer: React.FC<PropsWithChildren<Props>> = ({
  open,
  title,
  onClose,
  children,
  actions,
  size = 'md',
  innerScroll = false,
}) => {
  return (
    <DrawerContainer
      anchor="right"
      open={open}
      onClose={() => {
        onClose?.();
      }}
      size={size}
    >
      <DrawerTitle centered justify="space-between">
        {typeof title === 'string' ? (
          <>
            <Typography variant="h4">{title}</Typography>
            <CloseButton onClose={onClose} />
          </>
        ) : (
          title
        )}
      </DrawerTitle>
      <DrawerContent innerScroll={innerScroll}>{children}</DrawerContent>
      <DrawerActions>{actions}</DrawerActions>
    </DrawerContainer>
  );
};

export default FullPageDrawer;
